import React, { useEffect, useState } from 'react';
import { Button, Empty, Spin } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import { AnyObject } from '@triare/auth-redux';
import { useLayoutContext } from '../../Layout/context';
import ContentHeader from '../../Common/ContentHeader';
import InputSearch from '../../Common/InputSearch';
import AnimatedHeight from '../../Common/AnimatedHeight';
import Filter from './Filter';
import Post from '../../Common/Post';
import { useUniqueData } from '../../Common/PostOrCommentTemplate';
import { PostOrderColumn, PostsGetParams, PostShort, usePostsGet } from '../../../hooks/api/posts';
import { useSearchParams } from '../../../hooks/useSearchParams';
import { getValidSearchParams } from '../../../utils';
import SortBy from '../../Common/SortBy';

import styles from './index.module.scss';

export const businessesListDefaultSearchParams: Partial<PostsGetParams> = {
  orderByColumn: PostOrderColumn.CREATED,
  orderBy: 'DESC',
  search: '',
  updatedAtStart: '',
  updatedAtEnd: '',
};

export default function Posts(): JSX.Element {
  const { searchParams } = useSearchParams();
  const { setBreadcrumbs } = useLayoutContext();
  const [isOpenFilter, setIsOpenFilter] = useState(false);
  const postsGet = usePostsGet();
  const [page, setPage] = useState(1);
  const [postListAcc] = useUniqueData<PostShort>(postsGet, page);

  const loadPosts = (newPage?: number) => {
    const params: AnyObject = {
      page: newPage || page,
      isShowHiddenComment: 'true',
      ...businessesListDefaultSearchParams,
      ...getValidSearchParams<PostsGetParams>('*', searchParams),
    };

    Object.keys(params).forEach((key) => {
      if (typeof params[key] === 'undefined' || params[key] === '') {
        delete params[key];
      }
    });

    postsGet.fetch(params);
  };

  useEffect(loadPosts, [page, searchParams]);

  useEffect(() => {
    setBreadcrumbs([{ title: 'Posts' }]);
  }, []);

  return (
    <>
      <ContentHeader title="All Posts">
        <div className={styles.formContainer}>
          <Button onClick={() => setIsOpenFilter((prevState) => !prevState)}>Filter</Button>
          <SortBy />
          <InputSearch />
        </div>
      </ContentHeader>

      <AnimatedHeight isOpen={isOpenFilter} className={styles.filterWrapper}>
        <Filter onClose={() => setIsOpenFilter(false)} />
      </AnimatedHeight>

      <div className={styles.gap} style={{ minHeight: 'calc(100vh - 128px)' }}>
        {!postsGet.loading && postListAcc.list.length === 0 && postsGet.data?.data.length === 0 ? (
          <Empty className={styles.marginEmptyPage} />
        ) : (
          postListAcc.list.map((post) => (
            <Post
              setPage={(newPage) => {
                setPage(newPage);
                loadPosts(1);
              }}
              key={post.id}
              data={post}
            />
          ))
        )}
      </div>

      {postListAcc.lastMeta?.hasNextPage && !postsGet.loading ? (
        <div
          role="none"
          onClick={(e) => {
            e.preventDefault();
            setPage(page + 1);
          }}
          className={styles.showMore}
        >
          Show more
        </div>
      ) : null}

      {postsGet.loading ? (
        <div className="bgSpin">
          <Spin indicator={<LoadingOutlined spin />} size="large" />
        </div>
      ) : null}
    </>
  );
}
